import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GitMerge, ShieldAlert } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { describeMoved } from "@/components/AccountHistory";

const label = (a) => a.facility_name || a.name || a.email;

function AccountCell({ a, onKeep, disabled }) {
  const c = a.counts;
  return (
    <div className="flex-1 min-w-[220px] rounded-xl border border-slate-200 p-4" data-testid={`dup-account-${a.user_id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-slate-900">{label(a)}</span>
        {a.is_admin && <Badge className="bg-slate-800 text-white hover:bg-slate-800">admin</Badge>}
        {a.credential_status && a.role === "professional" && <Badge variant="outline">{a.credential_status}</Badge>}
      </div>
      <div className="text-xs text-slate-500 mt-1 break-all">{a.email}</div>
      <div className="text-xs text-slate-500">{a.phone || "no phone"}{a.whatsapp ? ` · WhatsApp ${a.whatsapp}` : ""}</div>
      <div className="text-xs text-slate-500 font-mono mt-1">{a.user_id}</div>
      <div className="text-xs text-slate-600 mt-2">
        {c.documents} docs · {c.applications} applications · {c.shifts_posted} shifts · {c.ratings_received} ratings
      </div>
      <Button size="sm" variant="outline" disabled={disabled} onClick={onKeep} className="mt-3 rounded-full" data-testid={`dup-keep-${a.user_id}`}>
        Keep this one
      </Button>
    </div>
  );
}

function PairCard({ pair, idx, onPick }) {
  const [a, b] = pair.accounts;
  const blocked = a.is_admin || b.is_admin;
  return (
    <Card className="p-5 rounded-2xl border-slate-200 bg-white" data-testid={`dup-pair-${idx}`}>
      <div className="flex items-center gap-2 mb-3 text-sm">
        <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">Possible duplicate</Badge>
        <span className="text-slate-600">{pair.reason} · {pair.role}</span>
      </div>
      <div className="flex flex-wrap gap-3">
        <AccountCell a={a} disabled={blocked} onKeep={() => onPick(a, b)} />
        <AccountCell a={b} disabled={blocked} onKeep={() => onPick(b, a)} />
      </div>
      {blocked && (
        <div className="mt-3 flex items-center gap-2 text-xs text-slate-600" data-testid={`dup-admin-note-${idx}`}>
          <ShieldAlert className="w-3.5 h-3.5" /> Admin accounts can't be merged.
        </div>
      )}
    </Card>
  );
}

export function DuplicateAccounts({ onMerged }) {
  const [pairs, setPairs] = useState(null);
  const [pending, setPending] = useState(null);
  const load = async () => {
    try {
      setPairs((await api.get("/admin/duplicates")).data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load duplicates");
    }
  };
  useEffect(() => { load(); }, []);
  const merge = async () => {
    const { keep, other } = pending;
    try {
      const { data } = await api.post("/admin/accounts/merge", { keep_id: keep.user_id, merge_id: other.user_id });
      const moved = describeMoved(data.moved);
      toast.success(`Merged into ${label(keep)}${moved ? ` · moved ${moved}` : ""}`);
      setPending(null);
      await load();
      onMerged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Merge failed");
      setPending(null);
    }
  };
  return (
    <section data-testid="duplicates-section">
      <h3 className="font-display text-lg font-semibold mb-3">Possible duplicate accounts</h3>
      {pairs === null ? null : pairs.length === 0 ? (
        <div className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-2xl p-8 text-center" data-testid="duplicates-empty">No possible duplicates found.</div>
      ) : (
        <div className="space-y-4">{pairs.map((p, i) => <PairCard key={i} idx={i} pair={p} onPick={(keep, other) => setPending({ keep, other })} />)}</div>
      )}
      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent data-testid="merge-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Merge these accounts?</AlertDialogTitle>
            <AlertDialogDescription>
              {pending && <>Keep <b>{label(pending.keep)}</b> ({pending.keep.user_id}). Documents, shifts, applications, ratings and the WhatsApp link
              from <b>{label(pending.other)}</b> ({pending.other.user_id}) move to it. The other account is marked
              “merged into {pending.keep.user_id}” and hidden from lists. Nothing is deleted, but this can't be undone from the admin panel.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="merge-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={merge} className="bg-emerald-700 hover:bg-emerald-800" data-testid="merge-confirm">
              <GitMerge className="w-4 h-4 mr-1" /> Merge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
